/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Title } from '../../../../components/Typography/Title';
import { Toolbar } from '../../../../components/Toolbar';
import { Table } from '../../../../components/Table';
import { Container } from './styles';
import {
	Button,
	Dropdown,
	Form,
	Icon,
	PaginationProps,
	Popup,
} from 'semantic-ui-react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useDeleteSubarea } from '../../hooks/useDeleteSubarea';
import { FilterGroup } from '../../../../components/Form/FilterGroup';
import { Controller, useForm } from 'react-hook-form';
import { useFindSubareas } from '../../hooks/useFindSubareas';
import { FilterListSubareas } from '../../types/subarea.type';
import { NoResultsTableInformation } from '../../../../components/TableNoDataFound';
import { useTranslation } from 'react-i18next';
import { ModalConfirm } from '../../../../components/ModalConfirm';
import { findAreaRequest } from '../../../areas/requests/findAreaRequest';
import { toDropdownItemProps } from '../../../../utils/toDropdownItemProps';
import { useQuery } from '@tanstack/react-query';

export function ListSubareas() {
	// @ts-ignore
	const { t } = useTranslation();
	const [openModal, setOpenModal] = useState<boolean>(false);
	const [item, setItem] = useState<number>();

	const { control, handleSubmit, reset } = useForm<FilterListSubareas>();
	const [page, setPage] = useState(1);
	const [filter, setFilter] = useState<FilterListSubareas>();

	const navigate = useNavigate();
	const location = useLocation();

	const { deleteSubarea } = useDeleteSubarea();

	const { listSubareas } = useFindSubareas({ page, filter });

	const { data: areaOptions } = useQuery(['AREA_OPTIONS'], async () => {
		const response = await findAreaRequest({});

		return toDropdownItemProps(response._embedded.areas);
	});

	const handlePageChange = (
		_: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
		data: PaginationProps
	) => {
		setPage(Number(data.activePage));
	};

	const onSubmitFilter = (data: FilterListSubareas) => {
		setFilter(data);
		setPage(1);
	};

	const handleResetFilter = () => {
		reset({
			name: '',
			idArea: '',
		});
		setFilter({
			name: undefined,
			idArea: undefined,
		});
	};

	return (
		<Container>
			<Title>{t('title.subareas')}</Title>

			<Toolbar
				handleAdd={() => navigate(`${location.pathname}/create`)}
				filter={{
					content: (
						<FilterGroup>
							<Controller
								control={control}
								name="idArea"
								render={({ field: { value, onChange } }) => (
									<Form.Dropdown
										label={t('page.competencies.area')}
										selection
										options={areaOptions ?? []}
										value={value}
										onChange={(_, data) => onChange(data.value)}
									/>
								)}
							/>
							<Controller
								control={control}
								name="name"
								render={({ field: { value, onChange } }) => (
									<Form.Input
										label={t('page.subareas.subarea')}
										value={value}
										onChange={onChange}
									/>
								)}
							/>
						</FilterGroup>
					),
					onApply: handleSubmit(onSubmitFilter),
					onClear: handleResetFilter,
				}}
			></Toolbar>

			<span>
				<Table>
					<Table.Header>
						<Table.Row>
							<Table.HeaderCell content="ID" />

							<Table.HeaderCell content={t('page.subareas.area')} />

							<Table.HeaderCell content={t('page.subareas.subarea')} />

							<Table.HeaderCell isActionColumn>
								<Icon name="cog" size="large" />
							</Table.HeaderCell>
						</Table.Row>
					</Table.Header>

					<Table.Body>
						{listSubareas?._embedded?.subareas?.map(
							({ id, area, name }, index) => (
								<Table.Row key={id}>
									<Table.Cell content={index + 1} isIdColumn />
									<Table.Cell content={area.name} />
									<Table.Cell content={name} />
									<Table.Cell isActionColumn>
										<Dropdown
											pointing="right"
											trigger={<Icon name="ellipsis vertical" size="large" />}
											icon={null}
										>
											<Dropdown.Menu>
												<span className="actionColumn__buttonGroup">
													<Popup
														content={t('tooltip.edit')}
														trigger={
															<Button
																icon="pencil"
																size="mini"
																color="yellow"
																onClick={() =>
																	navigate(`${location.pathname}/update/${id}`)
																}
															/>
														}
													/>

													<Popup
														content={t('tooltip.delete')}
														trigger={
															<Button
																icon="trash"
																size="mini"
																color="red"
																onClick={() => {
																	setItem(id);
																	setOpenModal(true);
																}}
															/>
														}
													/>
												</span>
											</Dropdown.Menu>
										</Dropdown>
									</Table.Cell>
								</Table.Row>
							)
						)}
					</Table.Body>
				</Table>

				{listSubareas?._embedded?.subareas?.length ? (
					<Table.Pagination
						page={page}
						activePage={page}
						totalPages={listSubareas?.page.totalPages ?? 0}
						boundaryRange={1}
						siblingRange={0}
						onPageChange={handlePageChange}
						size="mini"
						firstItem={null}
						lastItem={null}
						secondary
					/>
				) : (
					<NoResultsTableInformation
						icon="dont"
						title={t('message.noDataFound.title')}
						subTitle={t('message.noDataFound.subTitle')}
					/>
				)}
			</span>
			<Outlet />

			<ModalConfirm
				open={openModal}
				onClose={() => setOpenModal(false)}
				onOpen={() => setOpenModal(true)}
				title={t('component.modal.header.confirmation')}
				description={t('component.modal.content.AreYouSureYouWantToDelete')}
				type="remove"
				size="mini"
				onConfirm={() => {
					item && deleteSubarea.mutateAsync(item);
					setOpenModal(false);
				}}
				onCancel={() => setOpenModal(false)}
			/>
		</Container>
	);
}
